"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;

var _Units = require("./Units.js");

/**
 * @module ol/proj/Projection
 */

/**
 * @typedef {Object} Options
 * @property {string} code The SRS identifier code, e.g. `EPSG:4326`.
 * @property {import("./Units.js").default|string} [units] Units. Required unless a
 * proj4 projection is defined for `code`.
 * @property {import("../extent.js").Extent} [extent] The validity extent for the SRS.
 * @property {string} [axisOrientation='enu'] The axis orientation as specified in Proj4.
 * @property {boolean} [global=false] Whether the projection is valid for the whole globe.
 * @property {number} [metersPerUnit] The meters per unit for the SRS.
 * If not provided, the `units` are used to get the meters per unit from the {@link module:ol/proj/Units~METERS_PER_UNIT}
 * lookup table.
 * @property {import("../extent.js").Extent} [worldExtent] The world extent for the SRS.
 * @property {function(number, import("../coordinate.js").Coordinate):number} [getPointResolution]
 * Function to determine resolution at a point. The function is called with a
 * `number` view resolution and a {@link module:ol/coordinate~Coordinate Coordinate} as arguments, and returns
 * the `number` resolution in projection units at the passed coordinate. If this is `undefined`,
 * the default {@link module:ol/proj.getPointResolution getPointResolution()} function will be used.
 */

/**
 * @classdesc
 * Projection definition class. One of these is created for each projection
 * supported in the application and stored in the {@link module:ol/proj} namespace.
 * You can use these in applications, but this is not required, as API params
 * and options use {@link module:ol/proj~ProjectionLike} which means the simple string
 * code will suffice.
 *
 * You can use {@link module:ol/proj.get} to retrieve the object for a particular
 * projection.
 *
 * The library includes definitions for `EPSG:4326` and `EPSG:3857`, together
 * with the following aliases:
 * * `EPSG:4326`: CRS:84, urn:ogc:def:crs:EPSG:6.6:4326,
 *     urn:ogc:def:crs:OGC:1.3:CRS84, urn:ogc:def:crs:OGC:2:84,
 *     http://www.opengis.net/gml/srs/epsg.xml#4326,
 *     urn:x-ogc:def:crs:EPSG:4326
 * * `EPSG:3857`: EPSG:102100, EPSG:102113, EPSG:900913,
 *     urn:ogc:def:crs:EPSG:6.18:3:3857,
 *     http://www.opengis.net/gml/srs/epsg.xml#3857
 *
 * If you use [proj4js](https://github.com/proj4js/proj4js), aliases can
 * be added using `proj4.defs()`. After all required projection definitions are
 * added, call the {@link module:ol/proj/proj4.register} function.
 *
 * @api
 */
var Projection =
/** @class */
function () {
  /**
   * @param {Options} options Projection options.
   */
  function Projection(options) {
    /**
     * @private
     * @type {string}
     */
    this.code_ = options.code;
    /**
     * Units of projected coordinates. When set to `TILE_PIXELS`, a
     * `this.extent_` and `this.worldExtent_` must be configured properly for each
     * tile.
     * @private
     * @type {import("./Units.js").default}
     */

    this.units_ =
    /** @type {import("./Units.js").default} */
    options.units;
    /**
     * Validity extent of the projection in projected coordinates. For projections
     * with `TILE_PIXELS` units, this is the extent of the tile in
     * tile pixel space.
     * @private
     * @type {import("../extent.js").Extent}
     */

    this.extent_ = options.extent !== undefined ? options.extent : null;
    /**
     * Extent of the world in EPSG:4326. For projections with
     * `TILE_PIXELS` units, this is the extent of the tile in
     * projected coordinate space.
     * @private
     * @type {import("../extent.js").Extent}
     */

    this.worldExtent_ = options.worldExtent !== undefined ? options.worldExtent : null;
    /**
     * @private
     * @type {string}
     */

    this.axisOrientation_ = options.axisOrientation !== undefined ? options.axisOrientation : 'enu';
    /**
     * @private
     * @type {boolean}
     */

    this.global_ = options.global !== undefined ? options.global : false;
    /**
     * @private
     * @type {boolean}
     */

    this.canWrapX_ = !!(this.global_ && this.extent_);
    /**
     * @private
     * @type {function(number, import("../coordinate.js").Coordinate):number|undefined}
     */

    this.getPointResolutionFunc_ = options.getPointResolution;
    /**
     * @private
     * @type {import("../tilegrid/TileGrid.js").default}
     */

    this.defaultTileGrid_ = null;
    /**
     * @private
     * @type {number|undefined}
     */

    this.metersPerUnit_ = options.metersPerUnit;
  }
  /**
   * @return {boolean} The projection is suitable for wrapping the x-axis
   */


  Projection.prototype.canWrapX = function () {
    return this.canWrapX_;
  };
  /**
   * Get the code for this projection, e.g. 'EPSG:4326'.
   * @return {string} Code.
   * @api
   */


  Projection.prototype.getCode = function () {
    return this.code_;
  };
  /**
   * Get the validity extent for this projection.
   * @return {import("../extent.js").Extent} Extent.
   * @api
   */


  Projection.prototype.getExtent = function () {
    return this.extent_;
  };
  /**
   * Get the units of this projection.
   * @return {import("./Units.js").default} Units.
   * @api
   */


  Projection.prototype.getUnits = function () {
    return this.units_;
  };
  /**
   * Get the amount of meters per unit of this projection.  If the projection is
   * not configured with `metersPerUnit` or a units identifier, the return is
   * `undefined`.
   * @return {number|undefined} Meters.
   * @api
   */


  Projection.prototype.getMetersPerUnit = function () {
    return this.metersPerUnit_ || _Units.METERS_PER_UNIT[this.units_];
  };
  /**
   * Get the world extent for this projection.
   * @return {import("../extent.js").Extent} Extent.
   * @api
   */


  Projection.prototype.getWorldExtent = function () {
    return this.worldExtent_;
  };
  /**
   * Get the axis orientation of this projection.
   * Example values are:
   * enu - the default easting, northing, elevation.
   * neu - northing, easting, up - useful for "lat/long" geographic coordinates,
   *     or south orientated transverse mercator.
   * wnu - westing, northing, up - some planetary coordinate systems have
   *     "west positive" coordinate systems
   * @return {string} Axis orientation.
   * @api
   */


  Projection.prototype.getAxisOrientation = function () {
    return this.axisOrientation_;
  };
  /**
   * Is this projection a global projection which spans the whole world?
   * @return {boolean} Whether the projection is global.
   * @api
   */


  Projection.prototype.isGlobal = function () {
    return this.global_;
  };
  /**
   * Set if the projection is a global projection which spans the whole world
   * @param {boolean} global Whether the projection is global.
   * @api
   */


  Projection.prototype.setGlobal = function (global) {
    this.global_ = global;
    this.canWrapX_ = !!(global && this.extent_);
  };
  /**
   * @return {import("../tilegrid/TileGrid.js").default} The default tile grid.
   */


  Projection.prototype.getDefaultTileGrid = function () {
    return this.defaultTileGrid_;
  };
  /**
   * @param {import("../tilegrid/TileGrid.js").default} tileGrid The default tile grid.
   */


  Projection.prototype.setDefaultTileGrid = function (tileGrid) {
    this.defaultTileGrid_ = tileGrid;
  };
  /**
   * Set the validity extent for this projection.
   * @param {import("../extent.js").Extent} extent Extent.
   * @api
   */


  Projection.prototype.setExtent = function (extent) {
    this.extent_ = extent;
    this.canWrapX_ = !!(this.global_ && extent);
  };
  /**
   * Set the world extent for this projection.
   * @param {import("../extent.js").Extent} worldExtent World extent
   *     [minlon, minlat, maxlon, maxlat].
   * @api
   */


  Projection.prototype.setWorldExtent = function (worldExtent) {
    this.worldExtent_ = worldExtent;
  };
  /**
   * Set the getPointResolution function (see {@link module:ol/proj.getPointResolution}
   * for this projection.
   * @param {function(number, import("../coordinate.js").Coordinate):number} func Function
   * @api
   */


  Projection.prototype.setGetPointResolution = function (func) {
    this.getPointResolutionFunc_ = func;
  };
  /**
   * Get the custom point resolution function for this projection (if set).
   * @return {function(number, import("../coordinate.js").Coordinate):number|undefined} The custom point
   * resolution function (if set).
   */


  Projection.prototype.getPointResolutionFunc = function () {
    return this.getPointResolutionFunc_;
  };

  return Projection;
}();

var _default = Projection;
exports["default"] = _default;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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